import React, { useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Icon, useToast } from "@familyzone/component-library";
import { FocusDTO, FocusValidationConflictError, createOrUpdateFocus } from "js/api/focus";
import { colours, toasts } from "utils/themeUtils";
import { LibraryDrawerView } from "../";
import { transformSelectedResourcesToMatchAPIContract, transformUpsertResonseToFocusItem } from "../utils";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useFocusStore } from "stores/v2/focusStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { EditingFocus } from "../FocusPage";
import { SelectableStudent, StudentCheckboxSelector } from "@/components/StudentCheckboxSelector/StudentCheckboxSelector";

export interface Props {
	closeDrawer: () => void;
	/**
	 * The focus that will be applied to the selected students
	 */
	focusToBeApplied: EditingFocus;
	/**
	 * `classIsActive` is used to determine whether the Apply button should say "Apply Focus" or "Preset Focus" when the `currentScreen` is Classroom.
	 */
	classIsActive?: boolean;
	bubbleAppliedOrUpdatedFocus?: (appliedFocus: FocusDTO) => void;
	jumpToView: (view: LibraryDrawerView) => void;
	handleUpsertFailure: (error: Error) => void;
}

export function SelectStudentsPage(props: Props): JSX.Element {
	const [isApplying, setIsApplying] = useState<boolean>(false);
	const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

	const userSessionStore = useUserSessionStore();
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();

	const { successToast } = useToast();

	const selectableStudents: SelectableStudent[] =
		selectedClassroomStore.students?.map((student) => ({
			id: student.stableIdV1,
			label: `${student.firstName} ${student.lastName}`,
		})) || [];

	function handleApplyFocus(saveToLibrary: boolean): void {
		setIsApplying(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(props.focusToBeApplied.resources),
			props.focusToBeApplied.tabsLocked || false,
			true,
			saveToLibrary,
			userSessionStore.getTeacherId() as string,
			props.focusToBeApplied.name,
			selectedClassroomStore.stableIdV1 as string,
			selectedStudentIds,
			props.focusToBeApplied?.id
		)
			.then((response) => {
				successToast({
					title: `Focus has been ${saveToLibrary ? "updated and " : ""}applied to your class.`,
					position: toasts.position,
					isClosable: true,
				});

				const focusItem = transformUpsertResonseToFocusItem(response);

				if (!props.focusToBeApplied.isOnTheFly) focusStore.upsertFocusItemInLibrary(focusItem);

				if (props.bubbleAppliedOrUpdatedFocus) props.bubbleAppliedOrUpdatedFocus(focusItem);
				props.closeDrawer();
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);

				// Take the teacher back to fix the invalid errors if there are any
				if (e instanceof FocusValidationConflictError) props.jumpToView(LibraryDrawerView.FocusView);
			})
			.finally(() => {
				setIsApplying(false);
			});
	}

	return (
		<>
			<Flex bg={colours.gray[100]} px={25} py={20} justifyContent={"space-between"} alignItems={"center"}>
				<Flex alignItems={"center"}>
					<Icon
						data-testid="go-back-button"
						icon={"fa-arrow-left"}
						variant="solid"
						mr={25}
						boxSize={5}
						cursor={"pointer"}
						onClick={() => props.jumpToView(LibraryDrawerView.FocusView)}
					/>
					<Text data-testid="drawer-heading" fontSize={"xl"} fontWeight={"medium"}>
						Select Students
					</Text>
				</Flex>
				<Icon
					data-testid="close-drawer-button"
					icon={"fa-times"}
					variant="solid"
					mr={8}
					boxSize={5}
					onClick={props.closeDrawer}
					cursor={"pointer"}
				/>
			</Flex>
			<Box flex={"1"} p={25}>
				<StudentCheckboxSelector students={selectableStudents} handleSelectionChange={setSelectedStudentIds} />
			</Box>
			<Flex bg={colours.gray[100]} py={15} justifyContent={"space-between"} alignItems={"center"} px={15}>
				<Box></Box>
				<Button
					data-testid="apply-focus-button"
					variant={"primary"}
					isDisabled={selectedStudentIds.length < 1}
					onClick={() => handleApplyFocus(!props.focusToBeApplied.isOnTheFly)}
					isLoading={isApplying}
				>
					<Icon icon={"fa-arrows-to-eye"} variant="solid" mr={8} boxSize={4} />
					{props.classIsActive ? "Apply Focus" : "Preset Focus"}
				</Button>
			</Flex>
		</>
	);
}
